<script lang="ts">
  import { onMount } from 'svelte';
  import { Text, Title } from '@lib/components';
  import type { ScoresBlock } from './types';
  import { marked } from 'marked';
  
  export let block: ScoresBlock;

  function parseMarkdown(content: string) {
    if (!content) return [];
    const tokens = marked.lexer(content);
    return tokens.map((token) => {
      if (token.type === 'heading') {
        return {
          type: 'heading',
          text: token.text,
          depth: token.depth,
        };
      }
      return {
        type: token.type,
        text: token.raw,
      };
    });
  }

  function getNormalizedScores(): Record<string, number> {
    if (typeof window === 'undefined') return {};
    try {
      const raw = sessionStorage.getItem('walku:weights');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.normalizedScores || {};
    } catch {
      return {};
    }
  }

  function getAxesForScores(): Array<{ id: string; label?: string }> {
    if (block.axis) return [{ id: block.axis, label: block.title }];
    if (Array.isArray(block.axes) && block.axes.length > 0) return block.axes;
    const scores = getNormalizedScores();
    return Object.keys(scores).map((id) => ({ id }));
  }

  function hasScoresData(): boolean {
    const axes = getAxesForScores();
    const scores = getNormalizedScores();
    if (!axes || axes.length === 0) return false;
    return axes.some((axis) => scores[axis.id] !== undefined);
  }

  $: processedContent = block.markdown && block.content
    ? parseMarkdown(block.content)
    : block.content ? [{ type: 'paragraph', text: block.content }] : [];

  $: axes = getAxesForScores();
  $: scores = getNormalizedScores();

  // Radar chart specific logic - only loaded when needed
  let radarCanvas: HTMLCanvasElement;
  let radarChart: any = null;

  async function initRadarChart() {
    if (!radarCanvas || block.style !== 'radar') return;

    // Dynamically import Chart.js only when radar style is used
    const { 
      Chart, 
      RadialLinearScale, 
      PointElement, 
      LineElement, 
      Filler, 
      Tooltip, 
      Legend,
      RadarController
    } = await import('chart.js');
    
    Chart.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

    const styleTarget = radarCanvas as unknown as Element;
    const themePrimary = getComputedStyle(styleTarget || document.documentElement).getPropertyValue('--theme-primary-color').trim();
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const chosenColor = isDark
      ? (block.darkColor || block.color || themePrimary)
      : (block.color || themePrimary);

    const areaRgb = parseColorToRgb(chosenColor || '#10b981');
    const areaRgbString = `${areaRgb.r}, ${areaRgb.g}, ${areaRgb.b}`;
    const textColor = getComputedStyle(styleTarget || document.documentElement).getPropertyValue('--theme-text-color').trim() || '#374151';
    
    const data = {
      labels: axes.map(axis => axis.label || axis.id),
      datasets: [{
        data: axes.map(axis => scores[axis.id] || 0),
        backgroundColor: `rgba(${areaRgbString}, 0.69)`,
        borderColor: textColor + '66',
        borderWidth: 1,
        pointBackgroundColor: `rgb(${areaRgbString})`,
        pointBorderColor: textColor + '66',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: textColor + '66',
        pointRadius: 6,
        pointHoverRadius: 8
      }]
    };
    
    const options = {
      responsive: true,
      maintainAspectRatio: true,
      layout: { padding: 20 },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      },
      scales: {
        r: {
          beginAtZero: true,
          max: 100,
          ticks: {
            stepSize: 20,
            showLabelBackdrop: false,
            font: { size: 10 },
            color: textColor + '66'
          },
          grid: { color: textColor + '1A' },
          angleLines: { color: textColor + '1A' },
          pointLabels: {
            font: { size: 12 },
            color: textColor + 'B3',
            padding: 15
          }
        }
      }
    };

    if (radarChart) {
      radarChart.destroy();
    }

    radarChart = new Chart(radarCanvas, {
      type: 'radar',
      data,
      options
    });
  }

  // Pie chart helpers
  function getPieSlicePath(centerX: number, centerY: number, radius: number, startAngle: number, endAngle: number): string {
    const x1 = centerX + radius * Math.cos(startAngle);
    const y1 = centerY + radius * Math.sin(startAngle);
    const x2 = centerX + radius * Math.cos(endAngle);
    const y2 = centerY + radius * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  }

  function getDefaultColors(): string[] {
    return ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'];
  }

  function getPieData() {
    const total = axes.reduce((sum, a) => sum + (scores[a.id] || 0), 0);
    let currentAngle = 0;
    
    return axes.map((axis, i) => {
      const score = scores[axis.id] || 0;
      const percentage = total > 0 ? score / total : 0;
      const startAngle = currentAngle;
      const endAngle = currentAngle + (percentage * 2 * Math.PI);
      currentAngle = endAngle;
      
      return {
        axis,
        score,
        percentage,
        startAngle: startAngle - Math.PI/2,
        endAngle: endAngle - Math.PI/2,
        color: block.colors?.[axis.id] || getDefaultColors()[i % getDefaultColors().length],
        labelAngle: (startAngle + endAngle) / 2 - Math.PI/2
      };
    });
  }

  function parseColorToRgb(input: string): { r: number; g: number; b: number } {
    const fallback = { r: 16, g: 185, b: 129 };
    if (!input) return fallback;
    let color = input.trim();

    if (color.startsWith('var(') && typeof window !== 'undefined') {
      const varName = color.slice(4, -1).trim();
      const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      if (resolved) return parseColorToRgb(resolved);
    }

    const hexMatch = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (hexMatch) {
      return {
        r: parseInt(hexMatch[1], 16),
        g: parseInt(hexMatch[2], 16),
        b: parseInt(hexMatch[3], 16)
      };
    }

    return fallback;
  }

  $: if (radarCanvas && block.style === 'radar') {
    initRadarChart();
  }

  onMount(() => {
    if (block.style === 'radar') {
      initRadarChart();
    }
  });
</script>

{#if block.content}
  {#if block.markdown}
    {#each processedContent as token}
      {#if token.type === 'heading'}
        <div class="title-wrapper">
          <Title
            level={('h' + Math.min(6, Math.max(1, (token as any).depth || 2))) as any}
            align={block.alignment || 'left'}>{token.text}</Title
          >
        </div>
      {:else if token.type === 'paragraph'}
        <div class="text-wrapper">
          <Text align={block.alignment || 'left'}>{token.text}</Text>
        </div>
      {/if}
    {/each}
  {:else}
    <div class="text-wrapper">
      <Text align={block.alignment || 'left'}>{block.content}</Text>
    </div>
  {/if}
{/if}

{#if (block.style || 'horizontal') === 'horizontal'}
  {#each axes as axis (axis.id)}
    {#if scores[axis.id] !== undefined}
      <div class="score-row">
        <div class="score-bar">
          <div
            class="score-fill"
            style:width={`${scores[axis.id]}%`}
            style:background-color={block.colors?.[axis.id] || block.color || 'var(--theme-primary-color)'}
          ></div>
        </div>
        {#if block.showValues}
          <div class="score-value">{scores[axis.id]}%</div>
        {/if}
      </div>
    {/if}
  {/each}
{:else if block.style === 'radar'}
  {#if hasScoresData()}
    <div class="chartjs-radar-wrapper">
      <canvas bind:this={radarCanvas} class="chartjs-radar-canvas"></canvas>
    </div>
  {/if}
{:else if block.style === 'pie'}
  <div class="pie-wrapper">
    <svg class="pie-chart" viewBox="-60 -60 120 120" role="img" aria-label="Pie chart">
      {#each getPieData() as slice, i (slice.axis.id)}
        {#if slice.score > 0}
          <path 
            d={getPieSlicePath(0, 0, 45, slice.startAngle, slice.endAngle)} 
            fill={slice.color}
            class="pie-slice"
          />
          {#if block.showValues && slice.percentage > 0.05}
            <text 
              x={Math.cos(slice.labelAngle) * 30} 
              y={Math.sin(slice.labelAngle) * 30} 
              class="pie-label" 
              text-anchor="middle" 
              dominant-baseline="middle"
            >
              {Math.round(slice.score)}
            </text>
          {/if}
        {/if}
      {/each}
    </svg>
    {#if block.showValues}
      <div class="pie-legend">
        {#each getPieData() as slice (slice.axis.id)}
          <div class="legend-item">
            <div class="legend-color" style:background-color={slice.color}></div>
            <span class="legend-label">{slice.axis.label || slice.axis.id}</span>
            <span class="legend-value">{slice.score}</span>
          </div>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style lang="postcss">
  .title-wrapper {
    margin-bottom: 1rem;
  }

  .text-wrapper {
    margin-bottom: 1rem;
  }

  .score-row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 0.75rem;
    margin: 0.5rem 0;
  }

  .score-bar {
    height: 10px;
    background: var(--bar-bg, rgba(0,0,0,0.08));
    border-radius: 999px;
    overflow: hidden;
  }

  .score-fill {
    height: 100%;
    background: var(--primary-color);
    border-radius: 999px;
    transition: width 300ms ease;
  }

  .score-value {
    font-variant-numeric: tabular-nums;
    min-width: 3ch;
    text-align: right;
  }

  .chartjs-radar-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 1rem 0;
    width: 100%;
  }

  .chartjs-radar-canvas {
    width: 100% !important;
    height: auto !important;
    max-width: 400px;
    max-height: 400px;
  }

  .pie-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin: 1rem 0;
  }

  .pie-chart {
    width: 200px;
    height: 200px;
  }

  .pie-slice {
    stroke: white;
    stroke-width: 2;
    transition: opacity 0.2s ease;
  }

  .pie-slice:hover {
    opacity: 0.8;
  }

  .pie-label {
    font-size: 11px;
    font-weight: 600;
    fill: white;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
  }

  .pie-legend {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.5rem;
    width: 100%;
    max-width: 300px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
  }

  .legend-color {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .legend-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .legend-value {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
</style>